import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../services/userService";
import { useSubscription } from "../providers/SubscriptionProvider";
import ComingSoon from "../components/ComingSoon";
import { FEATURE_GATES } from "../config/featureAccess";

import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  setDoc,
  doc,
  limit,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import icon from "../assets/whiteIcon.svg";
import {
  conversationService,
  Conversation,
} from "../services/conversationService";
import { Message } from "../services/geminiService";
import BottomNavBar from "../components/layout/BottomNav";
import { SparklesIcon } from "@heroicons/react/24/solid";
import Header from "../components/layout/Header";

const Chats: React.FC = () => {
  const [input, setInput] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    subscription: userSubscription,
    hasSubscription,
    isPremium,
    refreshSubscription,
  } = useSubscription();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [warningTitle, setWarningTitle] = useState<string>("");
  // Note: Athlete report context is now handled in Chat.tsx via context injection
  // This keeps the initial message clean and avoids duplication
  useEffect(() => {
    if (!isPremium) {
      navigate("/");
    }
  }, [isPremium, navigate]);
  // Auto-expand textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Load conversations on mount
  useEffect(() => {
    const loadConversations = async () => {
      if (!currentUser) return;

      setIsLoadingConversations(true);
      try {
        const userConversations =
          await conversationService.loadUserConversations(currentUser.uid);
        setConversations(userConversations);
      } catch (error) {
        console.error("Error loading conversations:", error);
      } finally {
        setIsLoadingConversations(false);
      }
    };

    loadConversations();
  }, [currentUser]);

  const handleStartChat = async () => {
    if (!input.trim() || !currentUser || isCreating) return;
    const hasStartedChatToday = await userService.getTodayConversation(
      currentUser.uid,
    );
    if (!isPremium && hasStartedChatToday) {
      setWarningTitle(FEATURE_GATES.aiChatLimit.title);
      setWarningMessage(FEATURE_GATES.aiChatLimit.description);
      setShowPremiumModal(true);
      return;
    }

    console.log("for some reason, it reached here");
    // return
    setIsCreating(true);

    try {
      const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
      console.log("Fourteen days ago =", fourteenDaysAgo);

      let contextRef;
      let contextId;

      // Check for existing valid context
      const existingContextsQuery = query(
        collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        where("isActive", "==", true),
        orderBy("createdAt", "desc"),
        limit(1),
      );

      const existingContextsSnapshot = await getDocs(existingContextsQuery);

      if (!existingContextsSnapshot.empty) {
        const latestContext = existingContextsSnapshot.docs[0];
        const contextData = latestContext.data();
        const contextAge = Date.now() - contextData.createdAt.toMillis();
        const contextDataRange = contextData.context?.summary?.dateRange?.from;

        // Check if context is still valid (less than 1 hour old AND covers the same time range)
        const isContextValid =
          contextAge < 60 * 60 * 1000 &&
          contextDataRange &&
          contextDataRange >= fourteenDaysAgo;

        if (isContextValid) {
          console.log("Using existing valid context");
          contextId = latestContext.id;
          contextRef = { id: contextId };
        } else {
          console.log("Existing context is stale, creating new one");

          // Deactivate old context
          await updateDoc(
            doc(
              db,
              `wizardConversations/${currentUser.uid}/contexts`,
              latestContext.id,
            ),
            {
              isActive: false,
            },
          );
        }
      }

      // Create empty context if no valid one exists (athlete report will be provided directly in messages)
      if (!contextId) {
        console.log(
          "Creating minimal context (athlete report provided in messages)",
        );

        // Create minimal context without raw data
        const chatContext = {
          userProfile: {
            userId: currentUser.uid,
            dataCollectedAt: Date.now(),
            note: "Athlete data provided via athlete report in individual messages",
          },
        };

        // Save context document
        contextRef = doc(
          collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        );
        await setDoc(contextRef, {
          context: chatContext,
          createdAt: serverTimestamp(),
          isActive: true,
        });

        contextId = contextRef.id;
      }

      // Create new conversation in Firestore
      const conversationsRef = collection(
        db,
        `wizardConversations/${currentUser.uid}/conversations`,
      );
      const conversationDoc = await addDoc(conversationsRef, {
        title:
          input.trim().substring(0, 50) +
          (input.trim().length > 50 ? "..." : ""),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        firstMessage: input.trim(),
        contextId: contextId,
      });

      const message: Message = {
        role: "user",
        content: input.trim(),
        timestamp: new Date(),
      };

      await conversationService.saveMessage(
        currentUser.uid,
        conversationDoc.id,
        message,
      );

      // Navigate to the chat page with the new conversation ID
      navigate(`/chats/${conversationDoc.id}`);
    } catch (error) {
      console.error("Error creating conversation:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleStartChat();
    }
  };

  const handleQuickStart = async (quickInput: string) => {
    setInput(quickInput);

    if (!quickInput.trim() || !currentUser || isCreating) return;
    const hasStartedChatToday = await userService.getTodayConversation(
      currentUser.uid,
    );
    if (!isPremium && hasStartedChatToday) {
      setWarningTitle(FEATURE_GATES.aiChatLimit.title);
      setWarningMessage(FEATURE_GATES.aiChatLimit.description);
      setShowPremiumModal(true);
      return;
    }
    setIsCreating(true);

    try {
      const fourteenDaysAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
      console.log("Fourteen days ago =", fourteenDaysAgo);

      let contextRef;
      let contextId;

      // Check for existing valid context
      const existingContextsQuery = query(
        collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        where("isActive", "==", true),
        orderBy("createdAt", "desc"),
        limit(1),
      );

      const existingContextsSnapshot = await getDocs(existingContextsQuery);

      if (!existingContextsSnapshot.empty) {
        const latestContext = existingContextsSnapshot.docs[0];
        const contextData = latestContext.data();
        const contextAge = Date.now() - contextData.createdAt.toMillis();
        const contextDataRange = contextData.context?.summary?.dateRange?.from;

        // Check if context is still valid (less than 1 hour old AND covers the same time range)
        const isContextValid =
          contextAge < 60 * 60 * 1000 &&
          contextDataRange &&
          contextDataRange >= fourteenDaysAgo;

        if (isContextValid) {
          console.log("Using existing valid context");
          contextId = latestContext.id;
          contextRef = { id: contextId };
        } else {
          console.log("Existing context is stale, creating new one");

          // Deactivate old context
          await updateDoc(
            doc(
              db,
              `wizardConversations/${currentUser.uid}/contexts`,
              latestContext.id,
            ),
            {
              isActive: false,
            },
          );
        }
      }

      // Create empty context if no valid one exists (athlete report will be provided directly in messages)
      if (!contextId) {
        console.log(
          "Creating minimal context (athlete report provided in messages)",
        );

        // Create minimal context without raw data
        const chatContext = {
          userProfile: {
            userId: currentUser.uid,
            dataCollectedAt: Date.now(),
            note: "Athlete data provided via athlete report in individual messages",
          },
        };

        // Save context document
        contextRef = doc(
          collection(db, `wizardConversations/${currentUser.uid}/contexts`),
        );
        await setDoc(contextRef, {
          context: chatContext,
          createdAt: serverTimestamp(),
          isActive: true,
        });

        contextId = contextRef.id;
      }

      // Create new conversation in Firestore
      const conversationsRef = collection(
        db,
        `wizardConversations/${currentUser.uid}/conversations`,
      );
      const conversationDoc = await addDoc(conversationsRef, {
        title:
          quickInput.trim().substring(0, 50) +
          (quickInput.trim().length > 50 ? "..." : ""),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        firstMessage: quickInput.trim(),
        contextId: contextId,
      });

      const message: Message = {
        role: "user",
        content: quickInput.trim(),
        timestamp: new Date(),
      };

      await conversationService.saveMessage(
        currentUser.uid,
        conversationDoc.id,
        message,
      );

      // Navigate to the chat page with the new conversation ID
      navigate(`/chats/${conversationDoc.id}`);
    } catch (error) {
      console.error("Error creating conversation:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsCreating(false);
    }
  };

  const firstName = userData?.name || "there";

  const formatChatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } else if (diffInHours < 168) {
      // 7 days
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const handleChatClick = (conversationId: string) => {
    navigate(`/chats/${conversationId}`);
  };

  const handleDeleteConversation = async (
    conversationId: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent navigation when clicking delete

    if (
      window.confirm(
        "Are you sure you want to delete this conversation? This action cannot be undone.",
      )
    ) {
      try {
        await conversationService.deleteConversation(
          currentUser!.uid,
          conversationId,
        );
        // Update the local state to remove the deleted conversation
        setConversations((prev) =>
          prev.filter((conv) => conv.id !== conversationId),
        );
      } catch (error) {
        console.error("Error deleting conversation:", error);
        alert("Failed to delete conversation. Please try again.");
      }
    }
  };

  return (
    <div className="flex min-h-[100dvh] relative w-full pt-16 pb-24">
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => {
                setWarningTitle("");
                setWarningMessage("");
                setShowPremiumModal(false);
              }}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon
              featureKey="aiChatLimit"
              title={warningTitle}
              description={warningMessage}
            />
          </div>
        </div>
      )}
      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed left-6 top-[74.5px] z-50 p-2 bg-base-300 rounded-full hover:bg-base-content/10 transition-colors"
      >
        <svg
          className="w-6 h-6 text-base-content"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isSidebarOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-[64px] h-[calc(100vh-64px)] bg-base-100 border-r border-base-200 z-40 transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } w-80`}
      >
        <div className="pt-5 pr-4 pb-3 pl-[72px]">
          <h2 className="text-sm font-geist font-medium text-base-content/50 uppercase tracking-wider">
            Chat History
          </h2>
        </div>

        <div className="overflow-y-auto h-[calc(100%-56px)] px-3">
          {isLoadingConversations ? (
            <div className="p-4 text-center text-base-content/40 text-sm font-geist">
              Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-base-content/40 font-geist">
                No conversations yet
              </p>
              <p className="text-xs text-base-content/30 font-geist mt-1">
                Start a new chat below
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className="group relative w-full text-left px-3 py-3 rounded-lg hover:bg-base-200/70 active:bg-base-200 transition-colors cursor-pointer"
                  onClick={() => handleChatClick(conversation.id!)}
                >
                  <div className="flex justify-between items-start gap-2">
                    <h3 className="text-sm font-medium text-base-content line-clamp-1 pr-6">
                      {conversation.title}
                    </h3>
                    <span className="text-[11px] text-base-content/40 flex-shrink-0 mt-0.5 font-geist">
                      {formatChatDate(conversation.updatedAt)}
                    </span>
                  </div>
                  {(conversation.lastMessage || conversation.firstMessage) && (
                    <p className="text-xs text-base-content/50 mt-1 line-clamp-1 font-geist">
                      {conversation.lastMessage || conversation.firstMessage}
                    </p>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={(e) =>
                      handleDeleteConversation(conversation.id!, e)
                    }
                    className="absolute right-2 top-2.5 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-red-500/10 rounded-md"
                    title="Delete conversation"
                  >
                    <svg
                      className="w-3.5 h-3.5 text-base-content/40 hover:text-red-500 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6">
        <div className="max-w-2xl w-full space-y-6 sm:space-y-8">
          <div className="space-y-2">
            <div className="flex items-center justify-start gap-3 px-1 sm:px-4">
              <h1 className="text-2xl sm:text-3xl font-grotesk font-medium text-base-content">
                Hi {firstName},
              </h1>
              <span className="bg-lime text-gray-800 text-xs font-semibold px-2 py-1 rounded-full ">
                beta
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-6 sm:gap-8 w-full px-1 sm:px-4">
            <div className="w-full flex flex-col bg-base-100 items-end gap-1 border border-base-300 rounded-xl p-2 sm:p-3 transition-all">
              <textarea
                rows={4}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="I'm Oly, you can ask me anything about your performance, recovery, or training..."
                className="w-full resize-none font-geist border-none bg-transparent focus:outline-none placeholder-base-content/50 text-sm sm:text-base font-light p-2 sm:p-4"
                disabled={isCreating}
                autoFocus
              />
              <button
                onClick={handleStartChat}
                disabled={!input.trim() || isCreating}
                className="p-2 bg-neutral rounded-lg text-base-content/50 hover:text-base-content/70 disabled:opacity-0 transition-colors flex-shrink-0"
              >
                <img src={icon} alt="PEAK logo" className="h-5 w-auto" />
              </button>
            </div>
            <div className="space-y-2 justify-center items-end">
              <p className="text-sm text-base-content/60 pb-2">
                Example Prompts
              </p>
              <button
                onClick={() => handleQuickStart("How can I recover quicker?")}
                className="border flex gap-2 items-center relative border-base-300 px-2 py-2 rounded-xl text-base-content/80 bg-base-100 text-sm hover:text-base-content hover:bottom-1 font-grotesk"
              >
                <SparklesIcon className="h-4 w-4"></SparklesIcon>
                How can I recover quicker?
              </button>
              <button
                onClick={() => handleQuickStart("Analyze my training load")}
                className="border flex gap-2 items-center relative border-base-300 px-2 py-2 rounded-xl text-base-content/80 bg-base-100 text-sm hover:text-base-content hover:bottom-1 font-grotesk"
              >
                <SparklesIcon className="h-4 w-4"></SparklesIcon>
                Analyze my training load
              </button>
            </div>
          </div>
        </div>
        <BottomNavBar></BottomNavBar>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Chats;
